import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { keyBy } from 'lodash';
import { useEntities } from 'cityads-v3/hooks/entities';
import { BannerRotatorList } from 'cityads-v3/types/bannerRotator';
import { getScrollbarSize } from 'cityads-v3/utils/htmlUtils';
import classes from './Preview.scss'

import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography';

const REGEXP_URL_FROM_EMBED = /src=\"([^\"]+)\"/
const IFRAME_EXPAND_SIZE = typeof window === 'undefined' ? 0 : getScrollbarSize();

export type PreviewProps = {
  id: number,
  customReload?: ReturnType<typeof useReducer>,
  dimensionsInfo?: ReturnType<typeof useState<string>>,
}

export default function Preview({
  id,
  customReload: [customReload, forceCustomReload] = [null, null],
  dimensionsInfo: [, setDimensionsInfo] = [null, null]
}: PreviewProps): JSX.Element {
  const { t } = useTranslation(['main', 'BannerRotator'])
  const [iframeWidth, setIframeWidth] = useState<number>(0)
  const [iframeHeight, setIframeHeight] = useState<number>(0)
  const [isLoadingError, setIsLoadingError] = useState<boolean>(false)
  const [reload, forceReload] = useReducer(r => r + 1, 0)
  const [bannerRotator] = useEntities<BannerRotatorList>('bannerRotatorTool');
  const bannerRotatorById = useMemo(() => keyBy(bannerRotator?.bannerRotator, 'id'), [bannerRotator?.bannerRotator])
  const url = REGEXP_URL_FROM_EMBED.exec(bannerRotatorById[id]?.embed_code)?.[1]

  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    const iframe = iframeRef.current;

    iframe.src = 'about:blank';
    iframe.onload = function () {
      const doc = iframe.contentDocument;
      const html = doc.documentElement;
      const body = doc.body;
      const range = doc.createRange();

      range.setStart(body, 0);

      while (body.firstElementChild) {
        // just in case, it should be blank anyway
        body.firstElementChild.remove();
      }

      body.style.margin = '0px';
      body.style.padding = '0px';

      body.appendChild(
        range.createContextualFragment('<div id="x123" style="display:inline-block"></div>')
      )

      var script = doc.createElement('script');
      script.type = "text/javascript";
      script.src = `${url}/async/1/dv/123`
      script.onerror = () => setIsLoadingError(true)
      script.onload = () => setIsLoadingError(false)
      body.appendChild(script);
    };
  }, [url, iframeRef, customReload ?? reload, setIsLoadingError]);


  /**
   * Подгонка размеров айфрейма под размер баннера
   */
  useEffect(() => {
    const timerId = setInterval(() => {
      const width = (iframeRef.current.contentDocument.documentElement.querySelector('#x123') as unknown as {offsetWidth:number}).offsetWidth;
      const height = (iframeRef.current.contentDocument.documentElement.querySelector('#x123') as unknown as {offsetHeight:number}).offsetHeight;
      setIframeWidth(width + IFRAME_EXPAND_SIZE)
      setIframeHeight(height + IFRAME_EXPAND_SIZE)
      if (height) {
        clearInterval(timerId)
      }
    }, 100)

    return () => {
      clearInterval(timerId)
    }
  }, [iframeRef, setIframeHeight, setIframeWidth, customReload ?? reload])

  useEffect(() => {
    setDimensionsInfo?.(`${iframeWidth - IFRAME_EXPAND_SIZE}x${iframeHeight - IFRAME_EXPAND_SIZE}`)
  }, [iframeWidth, iframeHeight, setDimensionsInfo])

  return (
    <>
      <div>
        <iframe
          className={classes.iframe}
          ref={iframeRef}
          style={{
            height: iframeHeight + 'px',
            width: iframeWidth + 'px'
          }}
        />
        {isLoadingError ? (
          <Typography>{t('BannerRotator:noOffers')}</Typography>
        ) : null}
      </div>

      <div className={classes.info}>
        {!setDimensionsInfo && (
          <Typography>
            {iframeWidth - IFRAME_EXPAND_SIZE && iframeHeight - IFRAME_EXPAND_SIZE ? (
              <>{iframeWidth - IFRAME_EXPAND_SIZE}x{iframeHeight - IFRAME_EXPAND_SIZE}</>
            ) : null}
          </Typography>
        )}
        {!forceCustomReload && (
          <Button onClick={forceReload}>
            {t('main:buttons.refresh')}
          </Button>
        )}
      </div>
    </>
  )
}
